@file:OptIn(ExperimentalUuidApi::class, ExperimentalCoroutinesApi::class)

package ai.wagemix.data.firebase

import ai.wagemix.data.WMAuthSyncDataSource
import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.data.WMBaseSyncDataSource
import ai.wagemix.data.WMCache
import ai.wagemix.data.WMLocalContextDataSource
import ai.wagemix.data.accountDataOrThrow
import ai.wagemix.data.throwAccountFailureUnlessSuccess
import ai.wagemix.data.create
import ai.wagemix.data.isOwnedBy
import ai.wagemix.net.client.WagemixClientAPI
import ai.wagemix.net.models.WMAccountCreate
import ai.wagemix.net.models.WMAuthActionToken
import ai.wagemix.net.models.WMPasswordResetConfirm
import ai.wagemix.net.models.WMPasswordResetRequest
import ai.wagemix.net.models.WMSession
import ai.wagemix.net.models.kxs
import ai.wagemix.net.models.of
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlin.time.Instant
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Firebase Authentication as the [WMAuthSyncDataSource]. From construction it mirrors the
 * provider's auth state and the signed-in user's `users/{uid}` document into [WMCache]; the
 * operations run through the guarded pipeline and write the cache themselves so an outcome is
 * visible before the provider's listener catches up.
 *
 * Signing in and out stays with Firebase. Creating the account, verifying the email and resetting
 * the password go through the Wagemix API ([clientAPI]), which sends its own emails
 * (accounts-and-email plan §2.5).
 */
fun WMFirebaseAuthSyncDataSource(
    auth: FirebaseAuth,
    firestore: FirebaseFirestore,
    clientAPI: WagemixClientAPI,
    cache: WMCache,
    localContext: WMLocalContextDataSource
): WMAuthSyncDataSource {
    return WMFirebaseAuthSyncDataSourceImpl(
        api = WMAuthFirebaseApi(auth, firestore),
        accounts = clientAPI,
        cache = cache,
        localContext = localContext
    )
}

internal class WMFirebaseAuthSyncDataSourceImpl(
    private val api: WMAuthFirebaseApi,
    private val accounts: WagemixClientAPI,
    private val cache: WMCache,
    localContext: WMLocalContextDataSource,
    scope: CoroutineScope = CoroutineScope(SupervisorJob())
): WMBaseSyncDataSource<WMAuthSyncEvent<*>>(localContext, scope), WMAuthSyncDataSource {

    /** The provider's state, mirrored for the life of the data source. */
    @Suppress("unused")
    private val mirror: Job = api.sessionFlow
        .onEach { session -> if (session == null) cache.deactivateUsers() else rememberSession(session) }
        .flatMapLatest { session ->
            when (session) {
                null -> flowOf(null)
                else -> api.userDocumentFlow(session.uid).map { document -> session.uid to document }
            }
        }
        .onEach { mirrored ->
            mirrored?.let { (uid, document) ->
                cache.modifyUser(uid) { current ->
                    current?.let { WMCache.Value.User.create(it.session, document?.userData, document?.subscription, it.active) }
                }
            }
        }
        .launchIn(scope)

    /** Puts [session] into the cache as the active user, keeping whatever document data was already mirrored. */
    private suspend fun rememberSession(session: WMSession) {
        cache.modifyUser(session.uid) { current ->
            WMCache.Value.User.create(session, current?.userData, current?.subscription, active = true)
        }
        cache.activateUser(session.uid)
    }

    override fun signIn(email: String, password: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SIGN_IN,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.SignIn.Initial(email, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.SignIn.Success(email, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.SignIn.Failure(email, error, sequenceId, additionalContext) },
                remote = { api.signIn(email, password) },
                local = { session -> rememberSession(session) }
            )
        }
    }

    override fun register(firstName: String, lastName: String, email: String, password: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_REGISTER,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.Register.Initial(email, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.Register.Success(email, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.Register.Failure(email, error, sequenceId, additionalContext) },
                remote = {
                    accounts.createAccount { body(WMAccountCreate.kxs(firstName, lastName, email, password)) }
                        .throwAccountFailureUnlessSuccess()
                    // The API made the account; signing in to it is Firebase's, like any other sign-in.
                    api.signIn(email, password)
                },
                local = { session -> rememberSession(session) }
            )
        }
    }

    override fun sendEmailVerification(additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SEND_VERIFICATION,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.SendEmailVerification.Initial(sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.SendEmailVerification.Success(sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.SendEmailVerification.Failure(error, sequenceId, additionalContext) },
                remote = {
                    accounts.resendEmailVerification().throwAccountFailureUnlessSuccess()
                    // A 204 means the email is verified already; the reload shows it.
                    api.reload(forceTokenRefresh = true)
                },
                local = { session -> session?.let { rememberSession(it) } }
            )
        }
    }

    override fun sendPasswordReset(email: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SEND_RESET,
                scope = email,
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.SendPasswordReset.Initial(email, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.SendPasswordReset.Success(email, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.SendPasswordReset.Failure(email, error, sequenceId, additionalContext) },
                remote = {
                    accounts.requestPasswordReset { body(WMPasswordResetRequest.kxs(email)) }
                        .throwAccountFailureUnlessSuccess()
                },
                local = { }
            )
        }
    }

    override fun verifyPasswordResetCode(code: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_VERIFY_RESET_CODE,
                scope = code,
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.VerifyPasswordResetCode.Initial(code, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.VerifyPasswordResetCode.Success(code, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.VerifyPasswordResetCode.Failure(code, error, sequenceId, additionalContext) },
                remote = {
                    accounts.verifyPasswordReset { body(WMAuthActionToken.kxs(code)) }.accountDataOrThrow().email
                },
                local = { email -> localContext.upsertPasswordResetEmail(code, email) }
            )
        }
    }

    override fun confirmPasswordReset(code: String, newPassword: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_CONFIRM_RESET,
                scope = code,
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.ConfirmPasswordReset.Initial(code, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.ConfirmPasswordReset.Success(code, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.ConfirmPasswordReset.Failure(code, error, sequenceId, additionalContext) },
                remote = {
                    accounts.confirmPasswordReset { body(WMPasswordResetConfirm.kxs(code, newPassword)) }
                        .throwAccountFailureUnlessSuccess()
                },
                local = { }
            )
        }
    }

    override fun applyActionCode(code: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_APPLY_ACTION_CODE,
                scope = code,
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.ApplyActionCode.Initial(code, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.ApplyActionCode.Success(code, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.ApplyActionCode.Failure(code, error, sequenceId, additionalContext) },
                remote = {
                    accounts.confirmEmailVerification { body(WMAuthActionToken.kxs(code)) }
                        .throwAccountFailureUnlessSuccess()
                    // Signed in here: the ID token must carry email_verified before the API sees it.
                    api.reload(forceTokenRefresh = true)
                },
                local = { session -> session?.let { rememberSession(it) } }
            )
        }
    }

    override fun signOut(additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SIGN_OUT,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.SignOut.Initial(sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.SignOut.Success(sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.SignOut.Failure(error, sequenceId, additionalContext) },
                remote = {
                    val uid = cache.retrieveActiveUser()?.data?.uid
                    api.signOut()
                    uid
                },
                local = { uid ->
                    cache.deactivateUsers()
                    // The signed-out user's unsaved mix is theirs; it does not stay behind for the next visitor.
                    if (uid != null && cache.retrieveMixDraft()?.data?.isOwnedBy(uid) == true) {
                        cache.deleteMixDraft()
                    }
                    uid?.let { localContext.clearUser(it) }
                }
            )
        }
    }

    override fun updateProfile(uid: String, firstName: String, lastName: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_UPDATE_PROFILE,
                scope = uid,
                sequenceId = sequenceId,
                initial = { WMAuthSyncEvent.UpdateProfile.Initial(uid, sequenceId, additionalContext) },
                success = { WMAuthSyncEvent.UpdateProfile.Success(uid, sequenceId, additionalContext) },
                failure = { error -> WMAuthSyncEvent.UpdateProfile.Failure(uid, error, sequenceId, additionalContext) },
                remote = { api.updateProfile(uid, firstName, lastName) },
                local = { session -> session?.let { rememberSession(it) } }
            )
        }
    }

    private companion object {
        const val OP_SIGN_IN = "auth.signIn"
        const val OP_REGISTER = "auth.register"
        const val OP_SEND_VERIFICATION = "auth.sendEmailVerification"
        const val OP_SEND_RESET = "auth.sendPasswordReset"
        const val OP_VERIFY_RESET_CODE = "auth.verifyPasswordResetCode"
        const val OP_CONFIRM_RESET = "auth.confirmPasswordReset"
        const val OP_APPLY_ACTION_CODE = "auth.applyActionCode"
        const val OP_SIGN_OUT = "auth.signOut"
        const val OP_UPDATE_PROFILE = "auth.updateProfile"
    }
}

/** The only code that touches the Firebase Auth SDK and the `users/{uid}` document. */
internal interface WMAuthFirebaseApi {
    /** The signed-in user, or `null`; re-emits on every auth state change. */
    val sessionFlow: Flow<WMSession?>
    fun userDocumentFlow(uid: String): Flow<WMUserDocumentValue?>
    suspend fun signIn(email: String, password: String): WMSession
    /** Re-reads the signed-in user; [forceTokenRefresh] also replaces the ID token, so its claims match. */
    suspend fun reload(forceTokenRefresh: Boolean = false): WMSession?
    /** Writes the names onto `users/{uid}` and the display name onto the provider; returns the refreshed session. */
    suspend fun updateProfile(uid: String, firstName: String, lastName: String): WMSession?
    suspend fun signOut()
}

internal fun WMAuthFirebaseApi(auth: FirebaseAuth, firestore: FirebaseFirestore): WMAuthFirebaseApi {
    return WMAuthFirebaseApiImpl(auth, firestore)
}

private class WMAuthFirebaseApiImpl(private val auth: FirebaseAuth, private val firestore: FirebaseFirestore): WMAuthFirebaseApi {

    override val sessionFlow: Flow<WMSession?>
        get() = auth.authStateChanged.map { it?.toSession() }

    override fun userDocumentFlow(uid: String): Flow<WMUserDocumentValue?> {
        return firestore.collection("users").document(uid).snapshots.map { snapshot ->
            if (snapshot.exists) snapshot.data<UserDocument>().toDomain() else null
        }
    }

    override suspend fun signIn(email: String, password: String): WMSession = authCall {
        val result = auth.signInWithEmailAndPassword(email = email, password = password)
        result.user?.toSession() ?: throw IllegalStateException("No user in the sign-in result")
    }

    override suspend fun reload(forceTokenRefresh: Boolean): WMSession? = authCall {
        val user = auth.currentUser ?: return@authCall null
        user.reload()
        if (forceTokenRefresh) {
            user.getIdToken(forceRefresh = true)
        }
        auth.currentUser?.toSession()
    }

    override suspend fun updateProfile(uid: String, firstName: String, lastName: String): WMSession? = authCall {
        firestore.collection("users").document(uid).set(
            data = mapOf("firstName" to firstName, "lastName" to lastName, "updatedAt" to FieldValue.serverTimestamp),
            merge = true
        )
        auth.currentUser?.updateProfile(displayName = "$firstName $lastName".trim())
        auth.currentUser?.toSession()
    }

    override suspend fun signOut() = authCall { auth.signOut() }
}

private fun FirebaseUser.toSession(): WMSession {
    // The provider reports the creation time in seconds since the epoch.
    val createdAt = metaData?.creationTime?.let { Instant.fromEpochMilliseconds((it * 1000).toLong()) }
    return WMSession.of(
        uid = uid,
        email = email ?: "",
        emailVerified = isEmailVerified,
        displayName = displayName,
        createdAt = createdAt,
    )
}
